import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { getApiErrorMessage } from "../../lib/api";
import { toast } from "sonner";
import { 
  Users, 
  Coins, 
  ShoppingCart, 
  Gift, 
  Store, 
  Megaphone,
  Trophy,
  FileBarChart,
  ArrowUpRight,
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { formatLevel, formatPoints, formatProfession } from "../../lib/display";

const COLORS = {
  Ouro: "#EAB308",
  Prata: "#94A3B8",
  Bronze: "#B45309",
};

const StatCard = ({ icon: Icon, label, value, subtext, color = "primary", onClick }) => {
  const content = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
          {label}
        </p>
        <p className="mt-2 text-3xl font-heading font-bold text-slate-900">
          {value}
        </p>
        {subtext && (
          <p className="mt-1 text-sm text-slate-500">{subtext}</p>
        )}
      </div>
      <div className="flex items-start gap-2">
        {onClick && <ArrowUpRight className="mt-0.5 h-4 w-4 text-slate-400 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-focus-visible:-translate-y-0.5 group-focus-visible:translate-x-0.5" />}
        <div className={`h-12 w-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`h-6 w-6 text-${color}`} />
        </div>
      </div>
    </div>
  );

  const className = "stat-card card-hover group relative w-full text-left transition-all duration-200 hover:-translate-y-1 hover:shadow-md active:translate-y-0 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";
  if (!onClick) return <div className={className} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{content}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className} cursor-pointer`}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
      aria-label={`Abrir ${label}`}
    >
      {content}
    </button>
  );
};

const RankingTable = ({ title, subtitle, ranking, pointsLabel }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
    <div className="p-6 border-b border-slate-100">
      <h2 className="font-heading font-semibold text-lg text-slate-900">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      )}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px]">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Posição
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Parceiro
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Profissão
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              {pointsLabel}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Nível
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {ranking.map((parceiro) => (
            <tr key={`${title}-${parceiro.parceiro_id || parceiro.posicao}`} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-4">
                <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full font-semibold text-sm ${
                  parceiro.posicao === 1
                    ? "bg-yellow-100 text-yellow-700"
                    : parceiro.posicao === 2
                      ? "bg-slate-100 text-slate-700"
                      : parceiro.posicao === 3
                        ? "bg-orange-100 text-orange-700"
                        : "bg-slate-50 text-slate-500"
                }`}>
                  {parceiro.posicao}
                </span>
              </td>
              <td className="px-6 py-4 font-medium text-slate-900">
                {parceiro.nome}
              </td>
              <td className="px-6 py-4 text-slate-600">
                {formatProfession(parceiro.profissao)}
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1">
                  <Coins className="h-4 w-4 text-yellow-500" />
                  <span className="font-semibold text-slate-900">
                    {formatPoints(parceiro.pontos)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  parceiro.nivel === "Ouro"
                    ? "level-badge-gold"
                    : parceiro.nivel === "Prata"
                      ? "level-badge-silver"
                      : "level-badge-bronze"
                }`}>
                  {formatLevel(parceiro.nivel)}
                </span>
              </td>
            </tr>
          ))}
          {ranking.length === 0 && (
            <tr>
              <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                Nenhum parceiro cadastrado ainda
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [rankingFaturamento, setRankingFaturamento] = useState([]);
  const [rankingPontuacao, setRankingPontuacao] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, rankingFaturamentoRes, rankingPontuacaoRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/ranking?tipo=faturamento&limit=5"),
        api.get("/ranking?tipo=pontuacao&limit=5"),
      ]);
      setStats(statsRes.data);
      setRankingFaturamento(rankingFaturamentoRes.data);
      setRankingPontuacao(rankingPontuacaoRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error(getApiErrorMessage(error, "Erro ao carregar dashboard."));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const chartData = rankingFaturamento.map(r => ({
    name: r.nome.split(" ")[0],
    pontos: r.pontos,
  }));

  const levelCounts = rankingPontuacao.reduce((acc, parceiro) => {
    const nivel = formatLevel(parceiro.nivel);
    acc[nivel] = (acc[nivel] || 0) + 1;
    return acc;
  }, {});

  const levelData = ["Ouro", "Prata", "Bronze"]
    .map((name) => ({ name, value: levelCounts[name] || 0 }))
    .filter((entry) => entry.value > 0);
  const hasLevelData = levelData.length > 0;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">
          Dashboard
        </h1>
        <p className="text-slate-600 mt-1">
          Visão geral do seu programa de incentivos
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          icon={Users}
          label="Parceiros Ativos"
          value={stats?.parceiros_ativos || 0}
          color="primary"
          onClick={() => navigate("/admin/parceiros")}
        />
        <StatCard
          icon={Coins}
          label="Pontos Gerados"
          value={formatPoints(stats?.pontos_gerados)}
          color="warning"
          onClick={() => navigate("/admin/vendas")}
        />
        <StatCard
          icon={ShoppingCart}
          label="Vendas Registradas"
          value={stats?.vendas_registradas || 0}
          subtext={`R$ ${(stats?.valor_vendas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          color="success"
          onClick={() => navigate("/admin/vendas")}
        />
        <StatCard
          icon={Gift}
          label="Resgates"
          value={stats?.resgates_realizados || 0}
          subtext={`${stats?.resgates_pendentes || 0} pendentes`}
          color="accent"
          onClick={() => navigate("/admin/resgates")}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Store}
          label="Lojas Ativas"
          value={stats?.lojas_ativas || 0}
          color="primary"
          onClick={() => navigate("/admin/lojas")}
        />
        <StatCard
          icon={Megaphone}
          label="Campanhas Ativas"
          value={stats?.campanhas_ativas || 0}
          color="accent"
          onClick={() => navigate("/admin/campanhas")}
        />
        <StatCard
          icon={Trophy}
          label="Rankings"
          value="Ver ranking"
          subtext="Faturamento e saldo atual"
          color="warning"
          onClick={() => document.getElementById("dashboard-rankings")?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />
        <StatCard
          icon={FileBarChart}
          label="Relatórios"
          value="Ver análises"
          subtext="Engajamento dos parceiros"
          color="success"
          onClick={() => navigate("/admin/relatorios")}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Partners Chart */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h2 className="font-heading font-semibold text-lg text-slate-900 mb-6">
            Top 5 por Faturamento
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#94A3B8" />
                <YAxis dataKey="name" type="category" stroke="#94A3B8" width={80} />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }} 
                />
                <Bar dataKey="pontos" fill="#2563EB" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Levels Distribution */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h2 className="font-heading font-semibold text-lg text-slate-900 mb-6">
            Distribuição por Nível
          </h2>
          <div className="h-64 flex items-center justify-center">
            {hasLevelData ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={levelData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name} ${value}`}
                  >
                    {levelData.map((entry) => (
                      <Cell key={entry.name} fill={COLORS[entry.name]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} parceiro${value === 1 ? "" : "s"}`, "Quantidade"]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-sm text-slate-500">
                Nenhum parceiro ativo ainda
              </div>
            )}
          </div>
          <div className="flex justify-center gap-6 mt-4">
            {levelData.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2">
                <div 
                  className="h-3 w-3 rounded-full" 
                  style={{ backgroundColor: COLORS[entry.name] }}
                />
                <span className="text-sm text-slate-600">
                  {entry.name}: {entry.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rankings */}
      <div id="dashboard-rankings" className="grid grid-cols-1 2xl:grid-cols-2 gap-6 scroll-mt-6">
        <RankingTable
          title="Ranking por Faturamento"
          subtitle="Pontos conquistados em vendas válidas"
          ranking={rankingFaturamento}
          pointsLabel="Faturamento"
        />
        <RankingTable
          title="Ranking por Pontuação Atual"
          subtitle="Saldo disponível após resgates e estornos"
          ranking={rankingPontuacao}
          pointsLabel="Saldo"
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
